import { useRef } from "react";
import { Printer, Volume2, Square, Eraser } from "lucide-react";
import { useA11y } from "@/context/A11yContext";
import { useStoredState } from "@/hooks/useStoredState";
import { toolById } from "@/data/tools";
import { printTools } from "@/lib/scroll";
import { unmarkStarted } from "@/lib/progress";
import { Reveal } from "@/components/Reveal";
import { TextField } from "@/components/tools/fields";
import { useToolNumber } from "@/components/workbook/numbering";

const META = { school: "", by: "", date: "" };

export const ToolShell = ({ id, tool: toolProp, onReset, children, metaFields = true }) => {
  const tool = toolProp || toolById(id);
  const n = useToolNumber(id, tool.n);
  const ref = useRef(null);
  const { speak, stop, speaking, canSpeak } = useA11y();
  const [meta, setMeta, resetMeta] = useStoredState(`${id}-meta`, META);
  const clear = () => {
    if (!window.confirm(`Clear everything you have entered in "${tool.title}"?`)) return;
    onReset?.();
    resetMeta();
    setTimeout(() => unmarkStarted(id), 0);
  };
  return (
    <Reveal as="section" id={id} aria-labelledby={`${id}-title`} data-testid={`tool-${id}`}
      className="tool-section scroll-mt-36 rounded-[1.75rem] border-2 border-line bg-paper p-4 sm:p-8 lg:p-10">
      <div ref={ref}>
        <header className="flex flex-col gap-5 border-b border-line pb-6 md:flex-row md:items-start md:justify-between md:gap-6 md:pb-7">
          <div className="flex gap-3 sm:gap-5">
            <span className="font-display text-4xl font-light italic leading-none text-brand sm:text-6xl" aria-hidden="true">{n}</span>
            <div>
              <h3 id={`${id}-title`} className="font-display text-2xl font-medium leading-tight sm:text-3xl">{tool.title}</h3>
              <p className="mt-2 max-w-xl text-ink-soft">{tool.blurb}</p>
            </div>
          </div>
          <div className="no-print flex shrink-0 flex-wrap gap-2">
            {canSpeak && (
              <button type="button" className="btn btn-ghost btn-sm" data-testid={`${id}-listen`}
                onClick={() => (speaking ? stop() : speak(ref.current?.innerText || ""))}>
                {speaking ? <Square size={14} aria-hidden /> : <Volume2 size={14} aria-hidden />} {speaking ? "Stop" : "Listen"}
              </button>
            )}
            <button type="button" className="btn btn-brand btn-sm" onClick={() => printTools(id)} data-testid={`${id}-print`}>
              <Printer size={14} aria-hidden /> Print or save PDF
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={clear} data-testid={`${id}-clear`}>
              <Eraser size={14} aria-hidden /> Clear
            </button>
          </div>
        </header>
        {metaFields && (
          <div className="grid gap-4 border-b border-line py-6 sm:grid-cols-3">
            <TextField id={`${id}-school`} label="School" value={meta.school} onChange={(v) => setMeta({ ...meta, school: v })} testid={`${id}-meta-school`} />
            <TextField id={`${id}-by`} label="Completed by" value={meta.by} onChange={(v) => setMeta({ ...meta, by: v })} testid={`${id}-meta-by`} />
            <TextField id={`${id}-date`} label="Date" type="date" value={meta.date} onChange={(v) => setMeta({ ...meta, date: v })} testid={`${id}-meta-date`} />
          </div>
        )}
        <div className="pt-6">{children}</div>
        <p className="no-print mt-6 text-sm text-ink-soft">Do not enter children's names or any information that could identify a child. Your answers save automatically in this browser on this device only. Nothing is sent to us.</p>
      </div>
    </Reveal>
  );
};
